import React, { useMemo, useState, useEffect, useRef } from "react";
import { Tooltip } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import {
	PatientTreatmentPlan,
	PatientTreatmentPlanStageLog,
	PractitionerTaskType,
	StageLogStatus,
	StageType,
} from "../../../../../types/healthcare/TreatmentPlanTypes";
import {
	extractSectionsFromCanvas,
	buildSectionKeyByStageKey,
	buildSectionKeyMapFromCanvas,
	groupStagesBySection,
	ensureAllSections,
	trimBucketsAfterUndecidedDecision,
	isUndecidedDecisionNode,
	StageGroupItem,
	SECTION_GENERAL_KEY,
} from "./workflowProgress/sectionUtils";
import { buildAttemptExecutionOrder } from "./workflowProgress/executionOrder";
import { buildWatcherChildMap, buildWatcherParentMap } from "./workflowProgress/watcherMerge";
import { computeReachableStageKeys } from "./workflowProgress/branchFilter";
import {
	groupLogsByStageKey,
	currentAttemptByStageKey,
	pickActiveLog,
	attemptNumber,
} from "./workflowProgress/attemptSelection";

const HIDDEN: Set<StageType> = new Set([
	"trigger",
	"condition",
	"time_condition",
]);

interface DrawerStageListProps {
	plan: PatientTreatmentPlan;
	stageLogs: PatientTreatmentPlanStageLog[];
	selectedLogId: number | null;
	onSelectLog: (logId: number) => void;
}

export default function DrawerStageList({
	plan,
	stageLogs,
	selectedLogId,
	onSelectLog,
}: DrawerStageListProps) {
	const canvas = plan.template?.canvas_data ?? null;

	// Watcher → parent stage maps for the visual merge (frontend-only): merged watcher rows are
	// hidden from the list and surfaced on/inside the stage they watch.
	const watcherParent = useMemo(() => buildWatcherParentMap(canvas), [canvas]);
	const watcherChildByParent = useMemo(() => buildWatcherChildMap(canvas), [canvas]);
	const mergedWatcherKeys = useMemo(() => new Set(watcherParent.keys()), [watcherParent]);

	const { sectionBuckets, logsByKey, currentLogByKey } = useMemo(() => {
		// All attempts per stage_key (sorted by iteration), plus the CURRENT (highest-iteration)
		// attempt for the helpers that still need ONE representative log per stage.
		const logsByKey = groupLogsByStageKey(stageLogs);
		const currentLogByKey = currentAttemptByStageKey(stageLogs);

		const allNodes = canvas?.nodes ?? [];
		// Reachability drops the un-chosen branches of DECIDED decision points. Undecided decisions
		// keep every branch reachable here — the nodes that sit AFTER an undecided decision are hidden
		// SECTION-LOCALLY below (trimBucketsAfterUndecidedDecision) so a decision never hides later
		// section groups; all groups always render (ensureAllSections).
		const reachable = computeReachableStageKeys(canvas, stageLogs);
		// A node that has a stage log has actually been REACHED — always show it, regardless of the
		// canvas-only reachability (cyclic CoA segments + decided decisions can otherwise drop a node
		// the plan is genuinely sitting on). The execution history is ground truth; reachability only
		// adds the roadmap of FUTURE nodes.
		const loggedIds = new Set(logsByKey.keys());
		const visibleNodes = allNodes.filter(
			(n) =>
				n.type !== "section_group" &&
				!HIDDEN.has(n.data?.stage_type as StageType) &&
				!mergedWatcherKeys.has(n.id) &&
				(reachable.has(n.id) || loggedIds.has(n.id)),
		);

		// Per-ATTEMPT ordering: rank each individual log so loop re-entries sort after their first
		// visit; future (zero-log) nodes fall back to topological flow order.
		const { logOrder, nodeOrder } = buildAttemptExecutionOrder(canvas, stageLogs);

		// Expand each visible node into one row PER LOG (an attempt), plus a single zero-log "future"
		// row for not-yet-reached nodes. Logged rows order by their log's attempt rank; future rows by
		// the node's flow rank, with all logged (reached) rows before future ones.
		const loggedGroups: StageGroupItem[] = [];
		const futureGroups: StageGroupItem[] = [];
		visibleNodes.forEach((node) => {
			const logs = logsByKey.get(node.id) ?? [];
			if (logs.length === 0) futureGroups.push({ num: 0, nodes: [node] });
			else logs.forEach((log) => loggedGroups.push({ num: 0, nodes: [node], log }));
		});
		loggedGroups.sort(
			(a, b) => (logOrder.get(a.log!.id) ?? Infinity) - (logOrder.get(b.log!.id) ?? Infinity),
		);
		futureGroups.sort(
			(a, b) => (nodeOrder.get(a.nodes[0].id) ?? Infinity) - (nodeOrder.get(b.nodes[0].id) ?? Infinity),
		);
		const stageGroups = [...loggedGroups, ...futureGroups].map((g, i) => ({ ...g, num: i }));

		const sections = extractSectionsFromCanvas(canvas);
		const stageKeys = new Set(visibleNodes.map((n) => n.id));
		const canvasSectionMap = buildSectionKeyMapFromCanvas(canvas, stageKeys);
		const logSectionMap = buildSectionKeyByStageKey(stageLogs);

		// Prefer the CANVAS section (the section_group box a node sits in) — that is the layout the
		// practitioner designed. The snapshot/log section_key is derived from the backend section_id,
		// which on imported templates is often a single catch-all section, which would collapse every
		// stage into one group. Fall back to the log section only when the canvas has none.
		const merged = new Map<string, string | null>();
		visibleNodes.forEach((n) => {
			const fromCanvas = canvasSectionMap.get(n.id) ?? null;
			merged.set(n.id, fromCanvas ?? (logSectionMap.get(n.id) ?? null));
		});

		// Bucket the visible stages into sections, then (a) ensure EVERY section group renders even
		// when all its stages are currently gated, and (b) within each section hide the stages after
		// that section's own undecided decision point.
		const bucketed = groupStagesBySection(stageGroups, sections, merged);
		const withAllSections = ensureAllSections(bucketed, sections);
		const sectionBuckets = trimBucketsAfterUndecidedDecision(
			withAllSections,
			(node) => isUndecidedDecisionNode(node, currentLogByKey),
			(node) => loggedIds.has(node.id),
		);

		return { sectionBuckets, logsByKey, currentLogByKey };
	}, [canvas, stageLogs, mergedWatcherKeys]);

	// The log currently shown in Col 3
	const effectiveSelectedId = useMemo(() => {
		if (selectedLogId !== null) return selectedLogId;
		// Default to the CURRENT (highest-iteration) non-terminal attempt the plan is sitting on.
		const active = pickActiveLog(stageLogs);
		if (!active) return null;
		// If the active step is a merged watcher, highlight the stage it's joined to instead — the
		// watcher row itself is hidden, so its parent action row (its current attempt) represents it
		// in the list.
		const activeKey = active.stage_snapshot?.stage_key ?? active.stage?.stage_key;
		if (activeKey && mergedWatcherKeys.has(activeKey)) {
			const parentKey = watcherParent.get(activeKey);
			const parentLog = parentKey ? currentLogByKey.get(parentKey) : undefined;
			if (parentLog) return parentLog.id;
		}
		return active.id;
	}, [stageLogs, selectedLogId, mergedWatcherKeys, watcherParent, currentLogByKey]);

	// Which section contains the currently-shown stage
	const activeSectionKey = useMemo(() => {
		if (effectiveSelectedId === null) return null;
		const log = stageLogs.find((l) => l.id === effectiveSelectedId);
		if (!log) return null;
		const sectionKey = log.stage_snapshot?.section_key ?? null;
		return sectionKey ?? SECTION_GENERAL_KEY;
	}, [stageLogs, effectiveSelectedId]);

	// Collapse all sections except the active one on first open; auto-expand active
	// section when it changes thereafter.
	const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

	// Seed the initial collapsed state once, when sections first become available:
	// everything except the active section starts collapsed. Runs only once so it
	// never overrides sections the user later expands/collapses manually.
	const didInitCollapse = useRef(false);
	useEffect(() => {
		if (didInitCollapse.current || sectionBuckets.length === 0) return;
		didInitCollapse.current = true;
		const inactiveKeys = sectionBuckets
			.map((b) => b.panelKey)
			.filter((key) => key !== activeSectionKey);
		setCollapsed(new Set(inactiveKeys));
	}, [sectionBuckets, activeSectionKey]);

	useEffect(() => {
		if (activeSectionKey) {
			setCollapsed((prev) => {
				if (!prev.has(activeSectionKey)) return prev;
				const next = new Set(prev);
				next.delete(activeSectionKey);
				return next;
			});
		}
	}, [activeSectionKey]);

	const toggleSection = (key: string) => {
		setCollapsed((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	return (
		<div
			style={{
				width: 200,
				borderRight: "1px solid var(--mg-border)",
				overflowY: "auto",
				flexShrink: 0,
				display: "flex",
				flexDirection: "column",
			}}
		>
			{sectionBuckets.map((bucket) => {
				const isActiveSection = bucket.panelKey === activeSectionKey;
				const isCollapsed = collapsed.has(bucket.panelKey);
				const sectionLabel =
					bucket.panelKey === SECTION_GENERAL_KEY
						? "General"
						: (bucket.section?.name ?? "General");

				return (
					<div key={bucket.panelKey}>
						<div
							onClick={() => toggleSection(bucket.panelKey)}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 4,
								padding: "6px 8px",
								cursor: "pointer",

								background: isActiveSection
									? "rgba(24,144,255,0.04)"
									: "transparent",
								userSelect: "none",
							}}
						>
							<span
								style={{
									fontSize: 10,
									fontWeight: 600,
									textTransform: "uppercase",
									color: isActiveSection
										? "var(--mg-primary)"
										: "var(--mg-text-secondary)",
									flex: 1,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									letterSpacing: "0.04em",
								}}
							>
								{sectionLabel}
							</span>
							<span style={{ fontSize: 9, color: "var(--mg-text-disabled)", lineHeight: 1 }}>
								{isCollapsed ? <RightOutlined /> : <DownOutlined />}
							</span>
						</div>

						{!isCollapsed &&
							bucket.groups.map((group, groupIdx) => {
								const node = group.nodes[0];
								if (!node) return null;
								// Per-attempt row: this group renders its OWN log (a single loop attempt) — not a
								// last-wins lookup. Future (not-yet-reached) nodes carry no log.
								const log = group.log;
								const stageLabel = node.data?.label ?? node.data?.stage_key ?? node.id;
								const isSelected = log?.id === effectiveSelectedId;
								// Show an "Attempt N" badge ONLY when this stage has looped (more than one
								// attempt) — single-attempt stages render exactly as before, with no badge.
								const isMultiAttempt = (logsByKey.get(node.id)?.length ?? 0) > 1;

								// When a watcher is merged onto this stage, let its waiting/in-progress state
								// drive the row indicator so the practitioner sees the plan is paused here.
								const watcherKey = watcherChildByParent.get(node.id);
								const watcherLog = watcherKey ? currentLogByKey.get(watcherKey) : undefined;
								const displayStatus: StageLogStatus | undefined =
									log?.status === "completed" &&
										watcherLog &&
										(watcherLog.status === "waiting" || watcherLog.status === "in_progress")
										? watcherLog.status
										: log?.status;

								const iconColor = !log
									? "var(--mg-text-disabled)"
									: displayStatus === "completed" ? "var(--mg-success, #52c41a)"
										: displayStatus === "in_progress" ? "var(--mg-primary, #1890ff)"
											: displayStatus === "waiting" ? "var(--mg-warning, #fa8c16)"
												: displayStatus === "failed" ? "var(--mg-danger, #ff4d4f)"
													: displayStatus === "skipped" ? "var(--mg-text-disabled)"
														: "var(--mg-text-disabled)";

								return (
									<Tooltip
										key={log ? `log-${log.id}` : `node-${node.id}-${groupIdx}`}
										title={stageLabel}
										placement="right"
										mouseEnterDelay={0.6}
									>
										<div
											onClick={log ? () => onSelectLog(log.id) : undefined}
											style={{
												display: "flex",
												alignItems: "center",
												gap: 6,
												padding: "4px 8px 4px 12px",
												cursor: log ? "pointer" : "default",
												background: isSelected
													? "rgba(24,144,255,0.08)"
													: "transparent",
												borderLeft: isSelected
													? "2px solid var(--mg-primary)"
													: "2px solid transparent",
											}}
										>

											<span
												style={{
													fontSize: 11,
													color: log
														? "var(--mg-text)"
														: "var(--mg-text-disabled)",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													flex: 1,
												}}
											>
												{stageLabel}
											</span>
											{log && isMultiAttempt && (
												<span
													style={{
														flexShrink: 0,
														fontSize: 9,
														fontWeight: 600,
														padding: "0 4px",
														borderRadius: 4,
														color: "var(--mg-text-secondary)",
														background: "var(--mg-bg-subtle)",
														border: "1px solid var(--mg-border)",
														whiteSpace: "nowrap",
													}}
												>
													Attempt {attemptNumber(log)}
												</span>
											)}
											{log && (
												<span
													title={displayStatus}
													style={{
														flexShrink: 0,
														width: 8,
														height: 8,
														borderRadius: "50%",
														background: iconColor,
													}}
												/>
											)}
										</div>
									</Tooltip>
								);
							})}
					</div>
				);
			})}
		</div>
	);
}
